import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, X } from 'lucide-react';
import { OnlineUser, User, Affiliate, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { authService } from '../services/authService';
import { useAffiliateDirectory } from '../hooks/useAffiliateUserSearch';
import { PullToRefresh } from './PullToRefresh';
import { Skeleton } from './Skeleton';
import { PresenceDot } from './PresenceDot';

interface OnlineUsersPanelProps {
  onlineUsers: OnlineUser[];
  currentUser: User | null;
  onUserClick: (user: User) => void;
  /** 닫기 버튼. 모바일 '온라인' 탭처럼 닫을 대상이 없으면 생략 → 버튼 미표시 */
  onClose?: () => void;
}

/** 명부(오프라인 포함)와 실시간 상태를 합친 표시용 행 */
interface PanelUser {
  userId: string;
  userName: string;
  status: OnlineUser['status'];
  depart?: string | null;
  affiliateId?: string | null;
  avatar?: string | null;
}

const COMPANY_FALLBACK = '기타';
const DEPT_FALLBACK = '미배정';

const OnlineUsersPanel: React.FC<OnlineUsersPanelProps> = ({
  onlineUsers,
  currentUser,
  onUserClick,
  onClose,
}) => {
  // 계열사 ID → 라벨 매핑
  const [affiliateLabels, setAffiliateLabels] = useState<Record<string, string>>({});

  // 내 계열사 명부 — 오프라인 사용자의 유일한 출처.
  // 서버가 '재직(active) + 로그인 승인' 교집합만 내려준다.
  const myAffId = currentUser?.affiliateId;
  const { users: directory, loading: dirLoading, error: dirError } = useAffiliateDirectory(myAffId);

  // 회사 노드: 기존 의미('접힌 것을 담는 Set') 유지 → 기본 펼침
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set());
  // 부서 노드: 의미를 뒤집어 '펼친 것'을 담는다 → 기본 접힘
  //
  // ⚠ collapsed(접힌 것) 의미를 유지한 채 기본 접힘을 만들려면 트리 계산 후 전 부서 키를 seed해야
  //   하는데, 트리는 누가 접속하거나 나갈 때마다 새로 계산된다. 그러면 **펼쳐 둔 부서가 누군가
  //   접속할 때마다 다시 접히는** 버그가 나므로 '최초 1회' 가드와 신규 부서 diff 로직이 또 필요하다.
  //   Set의 의미를 뒤집으면 규칙이 한 줄로 끝난다.
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;
    authService.getAffiliates().then((list: Affiliate[]) => {
      if (!mounted) return;
      const map: Record<string, string> = {};
      list.forEach((a) => {
        map[a.id] = a.label;
      });
      setAffiliateLabels(map);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const toggleCompany = useCallback((key: string) => {
    setCollapsedCompanies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleDept = useCallback((key: string) => {
    setExpandedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // 접속자 인덱스 — 사번은 number|string이 섞이므로 String()으로 통일한다
  const onlineById = useMemo(() => {
    const map = new Map<string, OnlineUser>();
    onlineUsers.forEach((u) => {
      if (u.status !== 'offline') map.set(String(u.userId), u);
    });
    return map;
  }, [onlineUsers]);

  // 명부 + 실시간 상태 합성
  const people = useMemo<PanelUser[]>(() => {
    const seen = new Set<string>();
    const list: PanelUser[] = [];

    // ① 내 계열사: 명부 전원 (오프라인 포함)
    directory.forEach((u) => {
      const id = String(u.id);
      if (seen.has(id)) return;
      seen.add(id);
      const live = onlineById.get(id);
      list.push({
        userId: id,
        userName: u.username,
        status: live?.status ?? 'offline',
        depart: u.depart || live?.depart,          // 부서는 인사 원본(명부) 우선
        affiliateId: u.affiliateId || myAffId || null,
        avatar: live?.avatar ?? u.avatar ?? null,  // 아바타는 실시간 값 우선(서버 user_profiles 판정)
      });
    });

    // ② 타 계열사: 접속자만. 명부는 내 계열사만 조회할 수 있으므로(서버가 403) 오프라인은 알 수 없다.
    //    명부 로딩 중·조회 실패에도 접속자는 이 경로로 항상 보이고, 승인제 과도기처럼 "명부에는 없는데
    //    접속해 있는" 사람도 흡수한다 — 말을 걸 수 있는 사람이 목록에서 사라지는 쪽이 더 나쁜 실패다.
    onlineById.forEach((u, id) => {
      if (seen.has(id)) return;
      seen.add(id);
      list.push({
        userId: id,
        userName: u.userName,
        status: u.status,
        depart: u.depart,
        affiliateId: u.affiliateId,
        avatar: u.avatar ?? null,
      });
    });

    return list;
  }, [directory, onlineById, myAffId]);

  // 회사 → 부서 2단계 그룹화 + 노드별 접속/전체 카운트
  const tree = useMemo(() => {
    const companies = new Map<string, Map<string, PanelUser[]>>();

    people.forEach((u) => {
      const companyKey = u.affiliateId || COMPANY_FALLBACK;
      const deptKey = u.depart || DEPT_FALLBACK;
      if (!companies.has(companyKey)) companies.set(companyKey, new Map());
      const depts = companies.get(companyKey)!;
      if (!depts.has(deptKey)) depts.set(deptKey, []);
      depts.get(deptKey)!.push(u);
    });

    return Array.from(companies.entries())
      .sort((a, b) => {
        // 내 계열사를 맨 위로 — 유일하게 전원이 보이는 회사다
        if (a[0] === myAffId) return -1;
        if (b[0] === myAffId) return 1;
        return (affiliateLabels[a[0]] || a[0]).localeCompare(affiliateLabels[b[0]] || b[0]);
      })
      .map(([companyKey, deptMap]) => {
        const depts = Array.from(deptMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([deptKey, users]) => ({
            deptKey,
            // 온라인 먼저, 그 안에서 이름순
            users: [...users].sort((a, b) => {
              const ao = a.status === 'offline' ? 1 : 0;
              const bo = b.status === 'offline' ? 1 : 0;
              if (ao !== bo) return ao - bo;
              return a.userName.localeCompare(b.userName);
            }),
            onlineCount: users.filter((u) => u.status !== 'offline').length,
            total: users.length,
          }));
        return {
          companyKey,
          companyLabel: affiliateLabels[companyKey] || companyKey,
          isMine: companyKey === myAffId,
          depts,
          onlineCount: depts.reduce((sum, d) => sum + d.onlineCount, 0),
          total: depts.reduce((sum, d) => sum + d.total, 0),
        };
      });
  }, [people, affiliateLabels, myAffId]);

  const totalOnline = useMemo(() => people.filter((u) => u.status !== 'offline').length, [people]);
  const totalPeople = people.length;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* 헤더 */}
      <div className="h-14 md:h-16 flex items-center justify-between px-4 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <h1 className="text-lg font-bold text-slate-800 shrink-0">온라인</h1>
          <span
            className="text-sm text-slate-400"
            aria-label={`온라인 ${totalOnline}명, 전체 ${totalPeople}명`}
          >
            {totalOnline} <span className="text-slate-300">/ {totalPeople}</span>
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-10 h-10 -mr-1 flex items-center justify-center rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            aria-label="닫기"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* 트리 — 터치 기기에서는 상단에서 당기면 새로고침 */}
      <PullToRefresh className="flex-1 overflow-y-auto py-2">
        {dirError && tree.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium text-slate-500">동료 명부를 불러오지 못했습니다</p>
            <p className="mt-1 text-xs text-slate-400">잠시 후 다시 시도해 주세요</p>
          </div>
        ) : dirLoading && tree.length === 0 ? (
          <div className="px-3 py-2 space-y-2" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 rounded-lg" />
            ))}
          </div>
        ) : tree.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium text-slate-500">표시할 동료가 없습니다</p>
            <p className="mt-1 text-xs text-slate-400">관리자에게 문의해 주세요</p>
          </div>
        ) : (
          tree.map((company) => {
            const companyCollapsed = collapsedCompanies.has(`c:${company.companyKey}`);
            return (
              <div key={company.companyKey} className="mb-1">
                {/* 회사 노드 */}
                <button
                  type="button"
                  aria-expanded={!companyCollapsed}
                  onClick={() => toggleCompany(`c:${company.companyKey}`)}
                  className="w-full flex items-center gap-1 px-3 py-2 min-h-[40px] text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  {companyCollapsed ? (
                    <ChevronRight size={14} className="text-slate-400 shrink-0" />
                  ) : (
                    <ChevronDown size={14} className="text-slate-400 shrink-0" />
                  )}
                  <span className="truncate">{company.companyLabel}</span>
                  {/* 타 계열사는 접속자만 담긴 목록이라 '3 / 3'으로 쓰면 저 회사에 3명뿐인 것처럼 읽힌다 */}
                  <span
                    className="ml-auto text-xs font-normal text-slate-400"
                    aria-label={
                      company.isMine
                        ? `온라인 ${company.onlineCount}명, 전체 ${company.total}명`
                        : `온라인 ${company.onlineCount}명`
                    }
                  >
                    {company.onlineCount}
                    {company.isMine && <span className="text-slate-300"> / {company.total}</span>}
                  </span>
                </button>

                {/* 부서 노드 */}
                {!companyCollapsed &&
                  company.depts.map((dept) => {
                    const deptKey = `d:${company.companyKey}/${dept.deptKey}`;
                    const deptExpanded = expandedDepts.has(deptKey);
                    return (
                      <div key={deptKey}>
                        <button
                          type="button"
                          aria-expanded={deptExpanded}
                          onClick={() => toggleDept(deptKey)}
                          className="w-full flex items-center gap-1 pl-6 pr-3 py-1.5 min-h-[36px] text-xs font-medium text-slate-500 hover:bg-slate-50 transition-colors"
                        >
                          {deptExpanded ? (
                            <ChevronDown size={12} className="text-slate-300 shrink-0" />
                          ) : (
                            <ChevronRight size={12} className="text-slate-300 shrink-0" />
                          )}
                          <span className="truncate">{dept.deptKey}</span>
                          <span
                            className="ml-auto text-slate-400"
                            aria-label={`온라인 ${dept.onlineCount}명, 전체 ${dept.total}명`}
                          >
                            {dept.onlineCount}
                            <span className="text-slate-300"> / {dept.total}</span>
                          </span>
                        </button>

                        {/* 사용자 행 */}
                        {deptExpanded &&
                          dept.users.map((u) => {
                            const isMe =
                              !!currentUser && String(u.userId) === String(currentUser.id);
                            const isOffline = u.status === 'offline';
                            return (
                              <div
                                key={u.userId}
                                onClick={() => {
                                  if (isMe) return;
                                  // ⚠ 예전에는 { id, username }만 넘겨 초안 DM 헤더 아바타가 비어 있었다
                                  //   (handleUserClick이 dmPartnerAvatar에 user.avatar를 그대로 싣는다).
                                  onUserClick({
                                    id: u.userId,
                                    username: u.userName,
                                    avatar: u.avatar || '',
                                    depart: u.depart || undefined,
                                    affiliateId: u.affiliateId || undefined,
                                    status: u.status,
                                  } as User);
                                }}
                                className={`flex items-center gap-2 pl-10 pr-3 py-1.5 text-sm ${
                                  isMe
                                    ? 'text-slate-400 cursor-default'
                                    : isOffline
                                      ? 'text-slate-400 hover:bg-slate-50 cursor-pointer'
                                      : 'text-slate-600 hover:bg-slate-100 cursor-pointer'
                                }`}
                              >
                                <div className="relative shrink-0">
                                  <img
                                    src={resolveAvatarUrl(u.avatar)} onError={handleAvatarError}
                                    alt={u.userName}
                                    loading="lazy"
                                    decoding="async"
                                    className={`w-6 h-6 rounded-full object-cover bg-slate-200 ${
                                      isOffline ? 'opacity-50' : ''
                                    }`}
                                  />
                                  <PresenceDot
                                    status={u.status}
                                    labelled
                                    className="absolute -bottom-0.5 -right-0.5 w-2 h-2 border border-white"
                                  />
                                </div>
                                <span className="truncate">
                                  {u.userName}
                                  {isMe && ' (나)'}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })
        )}
      </PullToRefresh>
    </div>
  );
};

export default React.memo(OnlineUsersPanel);
